import { FC, useMemo } from 'react';

import { XIcon } from '@heroicons/react/outline';
import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';

const redeemStatusLabel: Record<GiftCouponRedeemStatus, string> = {
  [GiftCouponRedeemStatus.Available]: '사용가능',
  [GiftCouponRedeemStatus.Requested]: '교환신청',
  [GiftCouponRedeemStatus.Delivered]: '발송완료',
  [GiftCouponRedeemStatus.Expired]: '만료',
  [GiftCouponRedeemStatus.Refunded]: '환불',
  [GiftCouponRedeemStatus.Canceled]: '취소',
};

const redeemStatusColor: Record<GiftCouponRedeemStatus, string> = {
  [GiftCouponRedeemStatus.Available]: 'bg-blue-100 text-blue-800',
  [GiftCouponRedeemStatus.Requested]: 'bg-yellow-100 text-yellow-800',
  [GiftCouponRedeemStatus.Delivered]: 'bg-green-100 text-green-800',
  [GiftCouponRedeemStatus.Expired]: 'bg-gray-100 text-gray-500',
  [GiftCouponRedeemStatus.Refunded]: 'bg-red-100 text-red-800',
  [GiftCouponRedeemStatus.Canceled]: 'bg-gray-100 text-gray-500',
};

const shareStatusLabel: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: '미공유',
  [GiftCouponShareStatus.Shared]: '공유',
  [GiftCouponShareStatus.Viewed]: '열람',
};

const shareStatusColor: Record<GiftCouponShareStatus, string> = {
  [GiftCouponShareStatus.NotShared]: 'bg-gray-100 text-gray-500',
  [GiftCouponShareStatus.Shared]: 'bg-indigo-100 text-indigo-700',
  [GiftCouponShareStatus.Viewed]: 'bg-purple-100 text-purple-700',
};

const fmt = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const fmtTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}:${min}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const n = phone.replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  return phone;
};

interface CouponDetailModalProps {
  coupons: GiftCouponSummary[];
  onClose: () => void;
}

export const CouponDetailModal: FC<CouponDetailModalProps> = ({ coupons, onClose }) => {
  const sortedCoupons = useMemo(
    () => [...coupons].sort((a, b) => a.seq - b.seq),
    [coupons],
  );

  const stats = useMemo(() => {
    const total = coupons.length;
    const available = coupons.filter(
      (c) =>
        c.redeemStatus !== GiftCouponRedeemStatus.Delivered &&
        c.redeemStatus !== GiftCouponRedeemStatus.Refunded &&
        c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
    ).length;
    return { total, available };
  }, [coupons]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-gray-900">쿠폰 상세내역</h2>
            <span className="text-sm text-gray-500">
              미사용{' '}
              <span className="font-semibold text-blue-600">{stats.available}</span>/
              {stats.total}건
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="px-4 pb-4">
          {/* Table */}
          <div
            className="overflow-auto rounded-md border"
            style={{ maxHeight: 'calc(90vh - 56px)' }}
          >
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left text-gray-500">
                  <th className="whitespace-nowrap px-3 py-2 font-medium">#</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">코드</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">교환상태</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">구매일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">만료/교환일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">열람일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">신청자</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">신청일시</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">환불</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedCoupons.map((c) => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">
                      {c.seq}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono font-medium text-gray-800">
                      {c.code}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                          redeemStatusColor[c.redeemStatus]
                        }`}
                      >
                        {redeemStatusLabel[c.redeemStatus]}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5">
                      <span
                        className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                          shareStatusColor[c.shareStatus]
                        }`}
                      >
                        {shareStatusLabel[c.shareStatus]}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                      {fmt(c.purchasedAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5">
                      {c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
                      c.gifticon?.deliveredAt ? (
                        <span className="font-medium text-green-600">
                          {fmt(c.gifticon.deliveredAt)}
                        </span>
                      ) : (
                        <span className="text-gray-500">{fmt(c.expiresAt)}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                      {c.sharedAt ? fmt(c.sharedAt) : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                      {c.viewedAt ? fmt(c.viewedAt) : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">
                      {c.recipient ? (
                        <span>
                          {c.recipient.name}{' '}
                          <span className="text-gray-400">
                            {fmtPhone(c.recipient.phoneNumber)}
                          </span>
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                      {c.recipient ? fmtTime(c.recipient.requestedAt) : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5">
                      {c.refund ? (
                        <span className="text-red-600">
                          {c.refund.amount.toLocaleString()}원{' '}
                          <span className="text-red-400">{fmt(c.refund.refundedAt)}</span>
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
